import { type ConnectorSummary } from "../../lib/api";
import { cn } from "../../lib/cn";
import { getPlatformLogo } from "../../lib/platformLogos";
import { PlatformLogo } from "../PlatformLogo";

interface ConnectorsListProps {
  entries: [string, number][];
  connectorById: Map<string, ConnectorSummary>;
  onNavigate: (path: string) => void;
}

export function ConnectorsList({
  entries,
  connectorById,
  onNavigate,
}: Readonly<ConnectorsListProps>) {
  if (entries.length === 0) {
    return (
      <div className="px-5 py-10 text-center text-[13px] text-ash">
        No sources added yet.{" "}
        <button
          onClick={() => onNavigate("/connectors")}
          className="font-medium text-brand-700 hover:underline"
        >
          Browse connectors →
        </button>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-rule">
      {[...entries]
        .sort((a, b) => b[1] - a[1])
        .map(([connectorId, count]) => {
          const c = connectorById.get(connectorId);
          const hasLogo = !!getPlatformLogo(connectorId);
          return (
            <li
              key={connectorId}
              className="flex items-center gap-3.5 px-5 py-3.5 transition-colors hover:bg-paper"
            >
              <div
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg",
                  hasLogo ? "border border-rule bg-white" : "bg-brand-50 text-brand-600",
                )}
              >
                <PlatformLogo platformId={connectorId} alt={c?.displayName} className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold text-ink">
                  {c?.displayName ?? connectorId}
                </div>
                <div className="font-mono text-[10.5px] text-ash">{connectorId}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[15px] font-semibold text-ink" data-num>
                  {count}
                </div>
                <div className="font-mono text-[9.5px] uppercase tracking-wider text-ash">
                  {count === 1 ? "source" : "sources"}
                </div>
              </div>
            </li>
          );
        })}
    </ul>
  );
}
